import { FC, useMemo } from "react";
import Typography from "@mui/material/Typography";
import Slider, { sliderClasses } from "@mui/material/Slider";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import {
  useUnitInformation,
  ISortOption,
} from "../context/UnitsInformationProvider";
import { Icon } from "./Icon";

const CustomSlider = styled(Slider)(({ theme }) => ({
  [`& .${sliderClasses.valueLabelOpen}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

export const FilterInformation: FC<{ open: boolean }> = (props) => {
  const {
    unitGroups,
    sortOption,
    filters,
    setSortOption,
    selectFilter,
    priceRange,
    priceRangePossible,
    onPriceRangeChange,
    clearFilters,
  } = useUnitInformation();

  const handlePriceChange = (
    _event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (activeThumb === 0) {
      onPriceRangeChange([Math.min(newValue[0], priceRange[1]), priceRange[1]]);
    } else {
      onPriceRangeChange([priceRange[0], Math.max(newValue[1], priceRange[0])]);
    }
  };

  const uniqueUnitGroupsBySize = unitGroups.filter(
    (group, index, self) =>
      index ===
      self.findIndex(
        (g) => g.width === group.width && g.length === group.length
      )
  );

  const filtersCount = useMemo(() => {
    let count = filters.length;
    if (
      priceRange[0] !== priceRangePossible[0] ||
      priceRange[1] !== priceRangePossible[1]
    ) {
      count++;
    }
    return count;
  }, [filters, priceRange, priceRangePossible]);

  return (
    <Collapse in={props.open}>
      <Box sx={{ p: 1 }}>
        <Grid container>
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection="row-reverse"
              justifyContent="flex-start"
              alignItems="center"
              flexWrap="wrap"
            >
              {filtersCount > 0 && (
                <Button variant="text" onClick={clearFilters}>
                  Clear All
                </Button>
              )}
              {(priceRange[0] !== priceRangePossible[0] ||
                priceRange[1] !== priceRangePossible[1]) && (
                <Button
                  sx={(theme) => ({ m: 1, color: theme.palette.grey[500] })}
                  color="info"
                  variant="text"
                  onClick={() => {
                    onPriceRangeChange(priceRangePossible);
                  }}
                >
                  {`Price: $${priceRange[0]} - $${priceRange[1]}`}
                  <CloseIcon color="inherit" fontSize="inherit" />
                </Button>
              )}
              {filters.map((filter) => (
                <Button
                  key={filter.property + filter.value}
                  sx={(theme) => ({ m: 1, color: theme.palette.grey[500], fontWeight: 700 })}
                  color="info"
                  variant="text"
                  onClick={() => {
                    selectFilter(filter);
                  }}
                >
                  {filter.label}
                  <CloseIcon color="inherit" fontSize="inherit" />
                </Button>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography gutterBottom>Sort By</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <Select
                fullWidth
                size="small"
                id="demo-simple-select"
                value={sortOption.property}
                onChange={(ev) => {
                  setSortOption({
                    property: ev.target.value as ISortOption["property"],
                    order: "asc",
                    label: ev.target.value === "size" ? "Unit Size" : "Price",
                  });
                }}
                // onChange={handleChange}
              >
                <MenuItem value="size">Unit Size</MenuItem>
                <MenuItem value="price">Price</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography gutterBottom>Sizes</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={(theme) => ({
                flexDirection:
                  theme.direction === "ltr" ? "row" : "row-reverse",
                flexWrap: "wrap",
                mb: 1,
              })}
            >
              {uniqueUnitGroupsBySize.map((group) => (
                <Button
                  sx={{ mr: 1, mb: 1 }}
                  variant={
                    filters.find(
                      (x) =>
                        x.value === `${group.width}x${group.length}` &&
                        x.property === "size"
                    )
                      ? "contained"
                      : "outlined"
                  }
                  key={group.id}
                  onClick={() => {
                    selectFilter({
                      property: "size",
                      value: `${group.width}x${group.length}`,
                      label: `${group.width}’ x ${group.length}’`,
                    });
                  }}
                >
                  {`${`${group.width}’ x ${group.length}’`}`}
                </Button>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography gutterBottom>Amenities</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={(theme) => ({
                flexDirection:
                  theme.direction === "ltr" ? "row" : "row-reverse",
                flexWrap: "wrap",
                mb: 1,
              })}
            >
              {Array.from(
                new Set(
                  unitGroups.flatMap((group) =>
                    group.amenities.map((amenity) => amenity.name)
                  )
                )
              ).map((amenity: string) => (
                <Button
                  sx={{ mr: 1, mb: 1 }}
                  variant={
                    filters.find(
                      (x) => x.value === amenity && x.property === "amenities"
                    )
                      ? "contained"
                      : "outlined"
                  }
                  key={amenity}
                  onClick={() => {
                    selectFilter({
                      property: "amenities",
                      value: amenity,
                      label: amenity,
                    });
                  }}
                >
                  <Icon iconName={amenity.toLowerCase()} />
                  {amenity}
                </Button>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography gutterBottom>Price Range</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mt: 3 }}>
              <CustomSlider
                marks={[
                  {
                    value: priceRangePossible[0],
                    label: `$${priceRangePossible[0]}`,
                  },
                  {
                    value: priceRangePossible[1],
                    label: `$${priceRangePossible[1]}`,
                  },
                ]}
                // step={10}
                value={priceRange}
                min={priceRangePossible[0]}
                max={priceRangePossible[1]}
                onChange={handlePriceChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `$${value}`}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Collapse>
  );
};
